import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, ThumbsUp, ThumbsDown, X } from 'lucide-react';
import { getOrderDetail, rateOrder } from '../../services/p2pService';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

const FEEDBACK_TAGS = ['Fast transaction', 'Polite', 'Patient', 'Safe', 'Good price'];

function FeedbackSheet({ order, positive, onClose, onDone }: { order: any; positive: boolean; onClose: () => void; onDone: () => void }) {
  const { toast } = useToast();
  const [isPositive, setIsPositive] = useState(positive);
  const [tags, setTags] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [anon, setAnon] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await rateOrder(order._id, { score: isPositive ? 5 : 2, comment, tags, anonymous: anon });
      toast('Feedback submitted!', 'success');
      onDone();
    } catch (e: any) { toast(e.message, 'error'); }
    setLoading(false);
  };

  return (
    <>
      <div className="p2p-overlay" onClick={onClose} />
      <div className="p2p-bottom-sheet">
        <div className="p2p-sheet-handle" />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div className="p2p-sheet-title" style={{ margin: 0 }}>Rate Your P2P Experience</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--p2p-text-muted)' }}><X size={20} /></button>
        </div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <button
            onClick={() => setIsPositive(true)}
            style={{ flex: 1, height: 48, borderRadius: 8, border: `2px solid ${isPositive ? 'var(--p2p-buy)' : 'var(--p2p-border-default)'}`, background: isPositive ? 'rgba(28,212,167,0.1)' : 'var(--p2p-bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', color: isPositive ? 'var(--p2p-buy)' : 'var(--p2p-text-secondary)', fontWeight: 600 }}
          ><ThumbsUp size={16} />Positive</button>
          <button
            onClick={() => setIsPositive(false)}
            style={{ flex: 1, height: 48, borderRadius: 8, border: `2px solid ${!isPositive ? 'var(--p2p-sell)' : 'var(--p2p-border-default)'}`, background: !isPositive ? 'rgba(246,70,93,0.1)' : 'var(--p2p-bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', color: !isPositive ? 'var(--p2p-sell)' : 'var(--p2p-text-secondary)', fontWeight: 600 }}
          ><ThumbsDown size={16} />Negative</button>
        </div>
        <div className="p2p-chip-row" style={{ marginBottom: 16 }}>
          {FEEDBACK_TAGS.map(t => (
            <button key={t} className={`p2p-chip ${tags.includes(t) ? 'selected' : ''}`} onClick={() => setTags(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])}>{t}</button>
          ))}
        </div>
        <textarea
          className="p2p-input"
          style={{ minHeight: 80, resize: 'none', marginBottom: 12 }}
          placeholder="Leave a comment (optional)..."
          maxLength={500}
          value={comment}
          onChange={e => setComment(e.target.value)}
        />
        <label className="p2p-checkbox-row" style={{ marginBottom: 16 }}>
          <input type="checkbox" className="p2p-checkbox" checked={anon} onChange={e => setAnon(e.target.checked)} />
          <span style={{ fontSize: 13, color: 'var(--p2p-text-secondary)' }}>Submit anonymously</span>
        </label>
        <button className="p2p-btn-yellow" style={{ color: '#000' }} disabled={loading} onClick={submit}>
          {loading ? 'Submitting...' : 'Leave Feedback'}
        </button>
      </div>
    </>
  );
}

export default function OrderComplete() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackPositive, setFeedbackPositive] = useState(true);

  useEffect(() => { getOrderDetail(id!).then(setOrder).catch(() => {}).finally(() => setLoading(false)); }, [id]);

  const isBuyer = order && user && (order.buyer === user._id || order.buyer?._id === user._id);
  const hasRated = order && (isBuyer ? !!order.rating?.byBuyer?.score : !!order.rating?.bySeller?.score);
  const amountDisplay = isBuyer
    ? `+${order?.cryptoAmount?.toFixed(6) ?? '—'} ${order?.crypto ?? ''}`
    : `${order?.fiat ?? ''} ${order?.fiatAmount?.toLocaleString() ?? '—'}`;
  const subLabel = isBuyer
    ? 'Deposited into your Spot Account'
    : `Successfully sold ${order?.cryptoAmount?.toFixed(2) ?? '—'} ${order?.crypto ?? ''}`;

  const openFeedback = (pos: boolean) => { setFeedbackPositive(pos); setShowFeedback(true); };

  if (loading) return (
    <div style={{ background: 'var(--p2p-bg-primary)', minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 32px 32px', gap: 0 }}>
        <div className="p2p-skel" style={{ width: 88, height: 88, borderRadius: '50%', marginBottom: 24 }} />
        <div className="p2p-skel" style={{ width: 210, height: 40, borderRadius: 8, marginBottom: 12 }} />
        <div className="p2p-skel" style={{ width: 230, height: 16, borderRadius: 6, marginBottom: 32 }} />
        <div className="p2p-skel" style={{ width: 100, height: 18, borderRadius: 6 }} />
      </div>
      <div style={{ borderTop: '1px solid var(--p2p-border-default)', padding: '20px 24px 28px' }}>
        <div className="p2p-skel" style={{ width: 160, height: 16, borderRadius: 6, margin: '0 auto 18px' }} />
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div className="p2p-skel" style={{ flex: 1, height: 52, borderRadius: 10 }} />
          <div className="p2p-skel" style={{ flex: 1, height: 52, borderRadius: 10 }} />
        </div>
        <div className="p2p-skel" style={{ width: 180, height: 14, borderRadius: 6, margin: '0 auto' }} />
      </div>
    </div>
  );

  return (
    <div style={{
      background: 'var(--p2p-bg-primary)',
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
    }}>
      {/* Close */}
      <button
        onClick={() => navigate('/p2p', { replace: true })}
        style={{ position: 'absolute', top: 20, right: 20, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--p2p-text-muted)', zIndex: 1 }}
      >
        <X size={22} />
      </button>

      {/* Center content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 32px 32px', textAlign: 'center', gap: 0 }}>
        {/* Animated check circle */}
        <div style={{ width: 88, height: 88, borderRadius: '50%', background: 'rgba(28,212,167,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--p2p-buy)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Check size={32} color="#fff" strokeWidth={3} />
          </div>
        </div>

        <div style={{ fontSize: 38, fontWeight: 800, color: isBuyer ? 'var(--p2p-buy)' : 'var(--p2p-text-primary)', letterSpacing: '-0.5px', marginBottom: 8 }}>
          {order ? amountDisplay : '—'}
        </div>
        <div style={{ fontSize: 15, color: 'var(--p2p-text-muted)', marginBottom: 32 }}>
          {order ? subLabel : ''}
        </div>

        <button
          onClick={() => navigate('/dashboard/assets', { replace: true })}
          style={{ background: 'none', border: 'none', color: 'var(--p2p-warning)', fontSize: 16, fontWeight: 600, cursor: 'pointer', padding: '8px 20px' }}
        >
          Check Asset
        </button>
      </div>

      {/* Bottom review section */}
      <div style={{ borderTop: '1px solid var(--p2p-border-default)', padding: '20px 24px', paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))' }}>
        {hasRated ? (
          <div style={{ textAlign: 'center', color: 'var(--p2p-text-muted)', fontSize: 14, padding: '8px 0' }}>
            ✅ You've already submitted your review
          </div>
        ) : (
          <>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--p2p-text-primary)', marginBottom: 14, textAlign: 'center' }}>
              Review Counterparty
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <button
                onClick={() => openFeedback(true)}
                style={{ flex: 1, height: 52, borderRadius: 10, border: '1.5px solid var(--p2p-border-default)', background: 'var(--p2p-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', color: 'var(--p2p-text-primary)', fontWeight: 500, fontSize: 15 }}
              >
                <ThumbsUp size={18} color="var(--p2p-buy)" /> Positive
              </button>
              <button
                onClick={() => openFeedback(false)}
                style={{ flex: 1, height: 52, borderRadius: 10, border: '1.5px solid var(--p2p-border-default)', background: 'var(--p2p-bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', color: 'var(--p2p-text-primary)', fontWeight: 500, fontSize: 15 }}
              >
                <ThumbsDown size={18} color="var(--p2p-sell)" /> Negative
              </button>
            </div>
            <button
              onClick={() => openFeedback(true)}
              style={{ width: '100%', background: 'none', border: 'none', color: 'var(--p2p-warning)', cursor: 'pointer', fontSize: 14, fontWeight: 600, padding: '4px 0' }}
            >
              Rate Your P2P Experience
            </button>
          </>
        )}
      </div>

      {showFeedback && order && (
        <FeedbackSheet
          order={order}
          positive={feedbackPositive}
          onClose={() => setShowFeedback(false)}
          onDone={() => { setShowFeedback(false); getOrderDetail(id!).then(setOrder).catch(() => {}); }}
        />
      )}
    </div>
  );
}
